import React, { useState, useCallback, useEffect, useRef } from "react";
import { motion } from "motion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faSidebarFlip,
  faGear,
  faWindowRestore,
  faPlus,
  faArrowsLeftRight,
  faArrowLeft,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { isAxiosError } from "axios";
import { Chat, type ChatTimelineSegment } from "@/components/chat/Chat";
import { Logo } from "@/components/Logo";
import { useAni, type AniAnnouncement, type AniChatWidgetContextValue } from "@/context/AniContext";
import { buildFeAppStateForSend } from "@/components/chat/developerMenu/feAppState";
import { useRichAniContext } from "@/hooks/useRichAniContext";
import { useFloatingAniProfileCompanyId } from "@/hooks/useFloatingAniProfileCompanyId";
import { useFeAgentSession } from "@/hooks/useFeAgentSession";
import { useOsFocus, useOsWindowList, useOsOpenApps, useOsActions } from "./OsContext";
import {
  getOsGlassSurfaceClassName,
  OS_ANI_DOCK_CHAT_EDGE_SHADOW_CLASS,
  OS_ANI_DOCK_SIDEBAR_INNER_CLASS,
  OS_ANI_DOCK_SIDEBAR_SHELL_CLASS,
  OS_ANI_FLOATING_SHELL_CLASS,
  OS_ANI_SIDEBAR_INPUT_CLASS,
} from "./osWindowChrome";
import {
  buildOsAniContextSnapshot,
  type OsAniSnapshotSource,
} from "./buildOsAniContextSnapshot";
import { Button } from "@/components/ui/button/Button";
import {
  getInAppAgentMessages,
  createInAppAgentSession,
  startTurn,
  subscribeTurnEvents,
  submitFeToolResults,
  cancelTurn,
  type FeToolCall,
  type FeToolResult as InAppFeToolResult,
  type InAppAgentChatMessage,
} from "@/services/api/in-app-agent";
import {
  applyTurnEvent,
  initialLiveTurnState,
  isTurnThinking,
  turnFailureUserMessage,
  type LiveTimelineSegment,
  type LiveTurnState,
} from "@/services/inAppAgent/turnTimeline";
import type { ToolStep } from "@/components/chat/embeds/ToolStepRow";
import type { JsonObject } from "@/components/guardians/jsonValue";
import { useAuth } from "@/context/AuthContext";
import { useSamplePromptHouseholdName } from "@/hooks/useSamplePromptHouseholdName";
import { refreshInAppAgentContextForSend } from "@/services/inAppAgent/buildInAppAgentRuntimeContext";
import { resolveInAppAgentSamplePrompts } from "@/services/inAppAgent/resolveSamplePrompts";
import { parseChatEmbeds } from "@/components/chat/embeds/types";
import {
  fetchOpenworkInAppAgentContext,
  resolveOpenworkPersonaName,
} from "@/services/inAppAgent/fetchOpenworkInAppAgentContext";
import {
  getLatestOpenworkAgentContext,
  publishOpenworkAgentContext,
} from "@/services/inAppAgent/openworkAgentContextBridge";
import { getFeOpenAppAllowlist, listAccessibleAppIdsForFeContext } from "@/services/agent";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { isAniStaff } from "@/utils/aniPlatformAccess";
import { defaultChatContextConfigs } from "@/config/chatContexts";
import {
  ANI_CHAT_SIDEBAR_MIN_WIDTH_PX,
  ANI_CHAT_SIDEBAR_WIDTH_PX,
  ANI_CHAT_SIDEBAR_WIDE_WIDTH_PX,
  clampAniSidebarWidth,
} from "@/components/chat/chatMessageChrome";
import { osInteractionRef } from "./osInteractionRef";
import { AniFileDropZone } from "./AniFileDropZone";

const SIDEBAR_RESIZE_KEYBOARD_STEP_PX = 24;

interface ChatComponentMessage {
  id: string;
  content: string;
  role: "assistant" | "user";
  timestamp: number;
  sender: {
    id: string;
    name: string;
    type: string;
  };
  metadata?: {
    embeds?: ReturnType<typeof parseChatEmbeds>;
    tool_step?: ToolStep;
    timeline?: ChatTimelineSegment[];
    thinking?: boolean;
  };
}

/** Maps a live or persisted turn segment to the Chat timeline shape (same for reloads). */
function segmentToTimelineEntry(segment: LiveTimelineSegment): ChatTimelineSegment | null {
  if (segment.type === "text") {
    return { type: "text", content: segment.content };
  }
  if (segment.type === "tool_step" && segment.tool_name) {
    return {
      type: "tool_step",
      tool_name: segment.tool_name,
      label: segment.label,
      status: segment.status,
      summary: segment.summary ?? undefined,
      mcp_connector_id: segment.mcp_connector_id ?? undefined,
      step_id: segment.step_id,
      kind: segment.kind,
      fe_tool_id: segment.fe_tool_id,
      fe_tool_params: segment.fe_tool_params,
    };
  }
  if (segment.type === "embeds") {
    return { type: "embeds", embeds: parseChatEmbeds(segment.embeds) };
  }
  return null;
}

function toChatTimeline(segments: LiveTimelineSegment[]): ChatTimelineSegment[] {
  return segments
    .map(segmentToTimelineEntry)
    .filter((entry): entry is ChatTimelineSegment => entry !== null);
}

function buildLivePlaceholderMetadata(live: LiveTurnState): ChatComponentMessage["metadata"] {
  const metadata: ChatComponentMessage["metadata"] = {
    timeline: toChatTimeline(live.timeline),
    ...(live.status === "active"
      ? { thinking: isTurnThinking(live), thinking_status: live.thinkingStatus ?? undefined }
      : {}),
  };
  if (live.embeds.length > 0) {
    metadata.embeds = parseChatEmbeds(live.embeds);
  }
  return metadata;
}

function appendClientErrorTimelineText(live: LiveTurnState, content: string): LiveTurnState {
  return applyTurnEvent(live, {
    type: "turn.segment",
    segment_index: live.timeline.length,
    segment: { type: "text", content },
  });
}

function mapInAppMessageMetadata(metadata?: InAppAgentChatMessage["metadata"]): ChatComponentMessage["metadata"] {
  if (!metadata) return undefined;
  const mapped: ChatComponentMessage["metadata"] = {};
  if (metadata.embeds) {
    mapped.embeds = parseChatEmbeds(metadata.embeds);
  }
  if (metadata.tool_step?.tool_name) {
    mapped.tool_step = metadata.tool_step;
  }
  if (Array.isArray(metadata.timeline) && metadata.timeline.length > 0) {
    const timeline = toChatTimeline(metadata.timeline as LiveTimelineSegment[]);
    if (timeline.length > 0) {
      mapped.timeline = timeline;
    }
  }
  return Object.keys(mapped).length > 0 ? mapped : undefined;
}

function chatContextForOsApp(appId: string): AniChatWidgetContextValue {
  if (appId in defaultChatContextConfigs) {
    return appId as AniChatWidgetContextValue;
  }
  return undefined;
}

/**
 * OsAniSidebar - Persistent Ani assistant sidebar for OS layout
 * 
 * Supports two modes:
 * - Docked: Slides in from the right, pushing main content
 * - Floating: Draggable window that can be positioned anywhere
 */
export function OsAniSidebar() {
  const {
    widgetViewState,
    setWidgetViewState,
    setDisplayMode,
    setPersonalityMode,
    chatContext,
    apiData,
    initialAssistantMessage: contextInitialMessage,
    samplePrompts: contextSamplePrompts,
    appSamplePrompts,
    newSessionRequest,
    outboundUserMessage,
    clearOutboundUserMessage,
    aniAnnouncements,
    consumeAniAnnouncement,
    aniFileDropHandler,
  } = useAni();

  const { getFocusedWindow } = useOsFocus();
  const { workspaces, activeWorkspaceId, getWindow, getWindowsSnapshot, windowIdsByWorkspace } =
    useOsWindowList();
  const { focusedAppIdInActiveWorkspace } = useOsOpenApps();
  const osActionsApi = useOsActions();
  const currentApp = focusedAppIdInActiveWorkspace ?? "home";

  const baseRichContext = useRichAniContext({
    osFocusedApp: focusedAppIdInActiveWorkspace ?? undefined,
  });

  const osContext = buildOsAniContextSnapshot(
    workspaces,
    activeWorkspaceId,
    windowIdsByWorkspace,
    getWindow,
    getFocusedWindow,
    focusedAppIdInActiveWorkspace,
  );

  const { householdName: samplePromptHouseholdName } = useSamplePromptHouseholdName(osContext);

  const osSnapshotRef = useRef<OsAniSnapshotSource>({
    workspaces,
    activeWorkspaceId,
    windowIdsByWorkspace,
    getWindow,
    getWindowsSnapshot,
    getFocusedWindow,
    focusedAppIdInActiveWorkspace,
  });
  osSnapshotRef.current = {
    workspaces,
    activeWorkspaceId,
    windowIdsByWorkspace,
    getWindow,
    getWindowsSnapshot,
    getFocusedWindow,
    focusedAppIdInActiveWorkspace,
  };

  const osActionsRef = useRef(osActionsApi);
  osActionsRef.current = osActionsApi;
  const isAdmin = useIsAdmin();
  const { user } = useAuth();
  const aniStaff = isAniStaff(user);
  const canUseDeveloperMenu = isAdmin && aniStaff;
  const { profileCompanyId: staffProfileCompanyId } = useFloatingAniProfileCompanyId(canUseDeveloperMenu);
  const { hasPermission, permissions: modulePermissions } = useModulePermissions();
  const isOpenworkUser = (user?.company_id ?? "").toLowerCase() === "openworks";
  const openworkPersonaRef = useRef(resolveOpenworkPersonaName(user));
  openworkPersonaRef.current = resolveOpenworkPersonaName(user);

  // Keep a ref to the latest apiData
  const apiDataRef = useRef(apiData);
  apiDataRef.current = apiData;

  // Keep a ref to the latest personality mode
  const personalityModeRef = useRef(widgetViewState.personalityMode);
  personalityModeRef.current = widgetViewState.personalityMode;

  // Build richContext for the Chat component (used by Developer menu in dev mode)
  const richContext = {
    ...baseRichContext,
    route: `/${currentApp}`,
    // Replace URL inference with OS context
    osContext,
    urlInferredContext: undefined, // Don't show URL inference in OS mode
    // Include app state from AniContext
    appState: apiData,
  };

  // FE Agent session for tool execution
  const {
    executeTool,
    sessionId: feSessionId,
    createNewSession,
  } = useFeAgentSession();

  // Always enable chat - we're building an agent that should always be usable
  const isContextActive = true;

  const isOpen = widgetViewState.isOpen;
  const isFloating = widgetViewState.mode === "floating";
  const dockedWidth = widgetViewState.dockedWidth;
  const isDraggingRef = React.useRef(false);
  const [showContent, setShowContent] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [liveWidth, setLiveWidth] = useState(dockedWidth);
  const liveWidthRef = useRef(dockedWidth);
  const resizeDragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const renderedDockedWidth = isResizing ? liveWidth : dockedWidth;

  useEffect(() => {
    if (!isResizing) {
      setLiveWidth(dockedWidth);
      liveWidthRef.current = dockedWidth;
    }
  }, [dockedWidth, isResizing]);

  useEffect(() => {
    const onWindowResize = () => {
      const clamped = clampAniSidebarWidth(widgetViewState.dockedWidth, window.innerWidth);
      if (clamped !== widgetViewState.dockedWidth) {
        setWidgetViewState({ dockedWidth: clamped });
      }
    };
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [setWidgetViewState, widgetViewState.dockedWidth]);

  const commitDockedWidth = useCallback(
    (width: number) => {
      const clamped = clampAniSidebarWidth(width, window.innerWidth);
      setWidgetViewState({ dockedWidth: clamped });
    },
    [setWidgetViewState],
  );

  const endResizeDrag = useCallback(() => {
    if (!resizeDragRef.current) {
      return;
    }
    resizeDragRef.current = null;
    setIsResizing(false);
    osInteractionRef.current = false;
    document.body.style.userSelect = "";
    commitDockedWidth(liveWidthRef.current);
  }, [commitDockedWidth]);

  const handleResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (isFloating) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      resizeDragRef.current = { startX: event.clientX, startWidth: dockedWidth };
      setLiveWidth(dockedWidth);
      liveWidthRef.current = dockedWidth;
      setIsResizing(true);
      osInteractionRef.current = true;
      document.body.style.userSelect = "none";
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [dockedWidth, isFloating],
  );

  const handleResizePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeDragRef.current) {
      return;
    }
    const deltaX = resizeDragRef.current.startX - event.clientX;
    const nextWidth = clampAniSidebarWidth(
      resizeDragRef.current.startWidth + deltaX,
      window.innerWidth,
    );
    liveWidthRef.current = nextWidth;
    setLiveWidth(nextWidth);
  }, []);

  const handleResizePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!resizeDragRef.current) {
        return;
      }
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      endResizeDrag();
    },
    [endResizeDrag],
  );

  const handleResizeDoubleClick = useCallback(() => {
    commitDockedWidth(ANI_CHAT_SIDEBAR_WIDTH_PX);
  }, [commitDockedWidth]);

  const handleResizeKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }
      event.preventDefault();
      const delta = event.key === "ArrowLeft" ? SIDEBAR_RESIZE_KEYBOARD_STEP_PX : -SIDEBAR_RESIZE_KEYBOARD_STEP_PX;
      commitDockedWidth(dockedWidth + delta);
    },
    [commitDockedWidth, dockedWidth],
  );

  const handleToggleDockedWidth = useCallback(() => {
    const nextWidth =
      dockedWidth <= ANI_CHAT_SIDEBAR_WIDTH_PX
        ? ANI_CHAT_SIDEBAR_WIDE_WIDTH_PX
        : ANI_CHAT_SIDEBAR_WIDTH_PX;
    commitDockedWidth(nextWidth);
  }, [commitDockedWidth, dockedWidth]);

  const dockedWidthToggleTitle =
    dockedWidth <= ANI_CHAT_SIDEBAR_WIDTH_PX ? "Widen" : "Default width";

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1920;
  const maxDockedWidth = clampAniSidebarWidth(Number.MAX_SAFE_INTEGER, viewportWidth);


  // Track sessions that have been created on the backend (to avoid duplicate creation)
  const createdSessionsRef = useRef<Set<string>>(new Set());

  // Register the client fe_session_id on the API before history load or first message
  useEffect(() => {
    if (!feSessionId || createdSessionsRef.current.has(feSessionId)) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await createInAppAgentSession(undefined, feSessionId);
      } catch (sessionError) {
        if (!isAxiosError(sessionError) || sessionError.response?.status !== 403) {
          console.warn("[OsAniSidebar] Session ensure failed:", sessionError);
        }
      }
      if (!cancelled) {
        createdSessionsRef.current.add(feSessionId);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [feSessionId]);

  // Reset content visibility when closing
  React.useEffect(() => {
    if (!isOpen) {
      setShowContent(false);
    }
  }, [isOpen]);

  // Listen for new session requests from context (e.g., "Build Plan with Ani" button)
  const prevNewSessionRequest = useRef(newSessionRequest);
  useEffect(() => {
    if (newSessionRequest > prevNewSessionRequest.current) {
      // Announcements wait for the new chat to mount rather than landing in the old one.
      addMessageRef.current = null;
      createNewSession();
    }
    prevNewSessionRequest.current = newSessionRequest;
  }, [newSessionRequest, createNewSession]);

  // Context labels mapping
  const contextLabels: Record<string, string> = {
    scenarios: "Scenario Tracker",
    documents: "Documents",
    "cash-flow": "Cash Flow",
    pod: "Pod",
    dashboard: "Dashboard",
    screener: "Screener",
    playground: "Playground",
    product: "Product",
    portfolio: "Portfolio",
    worker: "Worker",
    "report-builder": "Report Builder",
    "case-checking": "Case Checking",
  };

  // Use focused app for context label, fallback to chatContext, then "General"
  const currentContextLabel = contextLabels[currentApp] || chatContext || "General";

  const focusedChatContext = chatContextForOsApp(currentApp);
  const focusedContextConfig = focusedChatContext
    ? defaultChatContextConfigs[focusedChatContext]
    : undefined;
  const effectiveSamplePrompts = resolveInAppAgentSamplePrompts(
    currentApp,
    contextSamplePrompts,
    appSamplePrompts,
    samplePromptHouseholdName,
  );
  const effectiveInitialMessage =
    contextInitialMessage ||
    (focusedContextConfig?.isActive ? focusedContextConfig.initialAssistantMessage : "") ||
    "";

  const handleClose = () => {
    setWidgetViewState({ isOpen: false });
  };

  const handleToggleFloat = () => {
    const newMode = isFloating ? "docked" : "floating";
    setWidgetViewState({
      mode: newMode,
    });
  };

  const handleNewChat = useCallback(() => {
    createNewSession();
  }, [createNewSession]);

  // Ref to trigger chat refresh from within sendMessage
  const refreshChatRef = useRef<(() => Promise<void>) | null>(null);
  // Ref to control typing indicator
  const setTypingRef = useRef<((isTyping: boolean) => void) | null>(null);
  // Ref to add messages (for tool banners)
  const addMessageRef = useRef<((message: ChatComponentMessage) => void) | null>(null);
  // Loading history replaces the chat's messages, so announcements wait until it has loaded.
  const feSessionIdRef = useRef(feSessionId);
  feSessionIdRef.current = feSessionId;
  const historyLoadedForRef = useRef<string | null>(null);

  // Ref to patch the assistant placeholder during SSE streaming
  const updatePlaceholderRef = useRef<
    ((updates: Pick<ChatComponentMessage, "content" | "metadata">) => void) | null
  >(null);

  const activeTurnStreamRef = useRef<{
    close: () => void;
    sessionId: string;
    turnId: string;
  } | null>(null);

  const closeActiveTurnStream = useCallback(() => {
    const active = activeTurnStreamRef.current;
    if (!active) {
      return;
    }
    active.close();
    activeTurnStreamRef.current = null;
  }, []);

  useEffect(() => {
    closeActiveTurnStream();
    updatePlaceholderRef.current = null;
  }, [feSessionId, closeActiveTurnStream]);

  useEffect(() => () => closeActiveTurnStream(), [closeActiveTurnStream]);

  const executeFeToolCalls = useCallback(
    async (feToolCalls: FeToolCall[]): Promise<InAppFeToolResult[]> => {
      const toolResults: InAppFeToolResult[] = [];
      for (const toolCall of feToolCalls) {
        const focusedWindow = toolCall.tool_id === "fe_show"
          ? osSnapshotRef.current.getFocusedWindow()
          : undefined;
        try {
          if (focusedWindow) {
            osActionsRef.current.updateWindow(focusedWindow.id, { isAniWorking: true });
            await new Promise((resolve) => setTimeout(resolve, 300));
          }

          const result = await executeTool(toolCall.tool_id, toolCall.params);
          toolResults.push({
            tool_id: toolCall.tool_id,
            success: result.success,
            result: result.data,
            error: result.success ? undefined : result.message,
          });

        } catch (error) {
          console.error(`[OsAniSidebar] Tool ${toolCall.tool_id} error:`, error);
          toolResults.push({
            tool_id: toolCall.tool_id,
            success: false,
            error: error instanceof Error ? error.message : "Unknown error",
          });
        } finally {
          if (focusedWindow) {
            osActionsRef.current.updateWindow(focusedWindow.id, { isAniWorking: false });
          }
        }
      }

      return toolResults;
    },
    [executeTool],
  );

  const executeFeToolCallsRef = useRef(executeFeToolCalls);
  executeFeToolCallsRef.current = executeFeToolCalls;

  const replayFeTool = useCallback((toolId: string, params: Record<string, unknown>) => {
    void executeFeToolCallsRef.current([
      { tool_id: toolId, params, instructions: undefined },
    ]);
  }, []);

  const playAnnouncement = useCallback(
    async ({ id, role, content, toolCall }: AniAnnouncement) => {
      setWidgetViewState({ isOpen: true });
      const chatReady = () => Boolean(addMessageRef.current) && historyLoadedForRef.current === feSessionIdRef.current;
      for (let tries = 0; !chatReady() && tries < 50; tries += 1) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (role === "assistant") {
        setTypingRef.current?.(true);
        await new Promise((resolve) => setTimeout(resolve, 900));
        setTypingRef.current?.(false);
      }
      addMessageRef.current?.({
        id,
        content,
        role,
        timestamp: Date.now(),
        sender: role === "assistant" ? { id: "ani", name: "Ani", type: "assistant" } : { id: user?.id ?? "user", name: "You", type: "user" },
      });
      if (toolCall) await executeFeToolCalls([toolCall]);
    },
    [executeFeToolCalls, setWidgetViewState, user?.id],
  );

  // Show queued announcements one at a time, in order.
  const nextAnnouncement = aniAnnouncements[0];
  const playingAnnouncementIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!nextAnnouncement || playingAnnouncementIdRef.current === nextAnnouncement.id) return;
    playingAnnouncementIdRef.current = nextAnnouncement.id;
    void playAnnouncement(nextAnnouncement)
      .catch((error) => console.warn("[OsAniSidebar] Announcement failed:", error))
      .finally(() => consumeAniAnnouncement(nextAnnouncement.id));
  }, [nextAnnouncement, playAnnouncement, consumeAniAnnouncement]);

  function renderSidebarContent() {
    return (
      <>
        {/* Header */}
        <div
          className={`flex h-12 flex-shrink-0 items-center justify-between p-2 px-4 ${isFloating
              ? "absolute left-0 right-0 top-0 z-20 cursor-move select-none bg-transparent"
              : OS_ANI_DOCK_SIDEBAR_INNER_CLASS
            }`}
          data-testid="ani-sidebar-header"
        >
          <div className="flex min-w-0 items-center">
            <Logo size="sm" className="cursor-default" data-testid="ani-sidebar-logo" />
          </div>

          <div
            className={`pointer-events-auto flex shrink-0 items-center space-x-1 ${
              isFloating ? "opacity-0 transition-opacity duration-200 group-hover:opacity-100" : ""
            }`}
          >
            {/* New Chat Button */}
            <Button
              type="button"
              plain
              data-testid="ani-sidebar-new-chat"
              onClick={handleNewChat}
              icon={<FontAwesomeIcon icon={faPlus} className="text-slate-600 dark:text-slate-300" />}
            >
              {""}
            </Button>

            {!isFloating && (
              <Button
                type="button"
                plain
                data-testid="ani-sidebar-width-toggle"
                onClick={handleToggleDockedWidth}
                title={dockedWidthToggleTitle}
                aria-label={dockedWidthToggleTitle}
                icon={
                  <FontAwesomeIcon icon={faArrowsLeftRight} className="text-slate-600 dark:text-slate-300" />
                }
              >
                {""}
              </Button>
            )}

            {/* Pop out / Dock Button */}
            <Button
              type="button"
              plain
              data-testid="ani-sidebar-float-toggle"
              onClick={handleToggleFloat}
              icon={
                <FontAwesomeIcon
                  icon={isFloating ? faSidebarFlip : faWindowRestore}
                  className="text-slate-600 dark:text-slate-300"
                />
              }
              title={isFloating ? "Dock sidebar" : "Pop out"}
            >
              {""}
            </Button>

            {canUseDeveloperMenu &&
              (widgetViewState.displayMode === "developer" ? (
                <Button
                  type="button"
                  plain
                  data-testid="ani-sidebar-exit-developer-menu"
                  aria-label="Back to chat"
                  title="Back to chat"
                  onClick={() => setDisplayMode("normal")}
                  icon={<FontAwesomeIcon icon={faArrowLeft} className="text-slate-600 dark:text-slate-300" />}
                >
                  {""}
                </Button>
              ) : (
                <Button
                  type="button"
                  plain
                  data-testid="ani-sidebar-settings"
                  aria-label="Open Developer menu"
                  title="Developer menu"
                  onClick={() => setDisplayMode("developer")}
                  icon={<FontAwesomeIcon icon={faGear} className="text-slate-600 dark:text-slate-300" />}
                >
                  {""}
                </Button>
              ))}

            <Button
              type="button"
              plain
              data-testid="ani-sidebar-close"
              onClick={handleClose}
              title="Close Ani"
              aria-label="Close Ani sidebar"
              icon={<FontAwesomeIcon icon={faTimes} className="text-slate-600 dark:text-slate-300" />}
            >
              {""}
            </Button>
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex min-h-0 flex-1 flex-col overflow-hidden ${OS_ANI_DOCK_SIDEBAR_INNER_CLASS}`}>
          <Chat
            key={feSessionId}
            recipientId="ani"
            recipientName="Ani"
            recipientAvatar="ani"
            conversationId={feSessionId}
            isAni={true}
            isMinimal={true}
            useDirectResponse={true}
            enableAssistantMarkdown={true}
            compactTimeline={true}
            chatChannel=""
            systemTaskChannel=""
            enableSystemJobs={false}
            miniUpload={true}
            isContextActive={isContextActive}
            richContext={richContext}
            contextLabel={currentContextLabel}
            displayMode={canUseDeveloperMenu ? widgetViewState.displayMode : "normal"}
            showMcpDebugPanel={canUseDeveloperMenu && widgetViewState.displayMode === "developer"}
            showFeToolDebugPanel={canUseDeveloperMenu && widgetViewState.displayMode === "developer"}
            feToolDebugRunner={executeTool}
            feToolDebugOsContext={osContext}
            feOpenAppAllowlist={getFeOpenAppAllowlist()}
            aniPersonalityMode={widgetViewState.personalityMode}
            onAniPersonalityModeChange={setPersonalityMode}
            outboundUserMessage={outboundUserMessage}
            onOutboundUserMessageConsumed={clearOutboundUserMessage}
            onReplayFeTool={replayFeTool}
            sendMessage={async (_recipientId, data): Promise<ChatComponentMessage> => {
              try {
                // Build fresh FE context using refs (always gets latest state)
                const buildFeContext = async () => {
                  const snap = osSnapshotRef.current;
                  const freshOsContext = {
                    ...buildOsAniContextSnapshot(
                      snap.workspaces,
                      snap.activeWorkspaceId,
                      snap.windowIdsByWorkspace,
                      snap.getWindow,
                      snap.getFocusedWindow,
                      snap.focusedAppIdInActiveWorkspace,
                    ),
                    available_app_ids: listAccessibleAppIdsForFeContext(hasPermission, { user, modulePermissions }),
                  };
                  const freshCurrentApp =
                    snap.focusedAppIdInActiveWorkspace ?? "home";

                  let agentBase =
                    getLatestOpenworkAgentContext() ??
                    (apiDataRef.current as { in_app_agent?: Record<string, unknown> } | undefined)
                      ?.in_app_agent;

                  if (isOpenworkUser && user?.company_id) {
                    try {
                      const fresh = await fetchOpenworkInAppAgentContext({
                        companyId: user.company_id,
                        personaName: openworkPersonaRef.current,
                        assigneeUserId: user.id,
                        assigneeEmail: user.email,
                      });
                      agentBase = fresh;
                      publishOpenworkAgentContext(fresh);
                    } catch (error) {
                      console.warn("[OsAniSidebar] OpenWork context refresh failed:", error);
                    }
                  }

                  const contextBase =
                    agentBase ??
                    (user?.company_id ? { company_id: user.company_id } : undefined);

                  const focusedWindow = freshOsContext.focusedWindow;
                  const appState = buildFeAppStateForSend(
                    (apiDataRef.current ?? {}) as JsonObject,
                    focusedWindow,
                  ) as JsonObject;

                  return {
                    os_context: freshOsContext,
                    current_app: freshCurrentApp,
                    app_state: appState,
                    personality_mode: personalityModeRef.current,
                    agent_context: refreshInAppAgentContextForSend(contextBase, freshOsContext),
                  };
                };

                // Ensure session exists on backend before sending the first message
                // This allows sessions to appear in chat history
                if (!createdSessionsRef.current.has(feSessionId)) {
                  try {
                    // Create session with a title derived from the first message
                    const titlePreview = data.content.substring(0, 50) + (data.content.length > 50 ? "..." : "");
                    await createInAppAgentSession(titlePreview, feSessionId);
                    createdSessionsRef.current.add(feSessionId);
                  } catch (sessionError) {
                    // 409 Conflict means session already exists — expected, ignore silently
                    if (!isAxiosError(sessionError) || sessionError.response?.status !== 409) {
                      console.warn("[OsAniSidebar] Session creation failed:", sessionError);
                    }
                    createdSessionsRef.current.add(feSessionId); // Mark as created to avoid retrying
                  }
                }

                // Start SSE-backed turn
                const turnStartedAtMs = Date.now();
                const { turn_id: turnId } = await startTurn(feSessionId, {
                  message_content: data.content,
                  fe_context: await buildFeContext(),
                  attachments: data.attachments,
                  ...(staffProfileCompanyId ? { profile_company_id: staffProfileCompanyId } : {}),
                });

                let live = initialLiveTurnState();
                let turnFailedMessage: string | null = null;
                let polledMetadata: ChatComponentMessage["metadata"];

                closeActiveTurnStream();
                const streamSessionId = feSessionId;
                const handledFeRequestIds = new Set<string>();

                const streamStillActive = () => {
                  const active = activeTurnStreamRef.current;
                  return (
                    active?.sessionId === streamSessionId &&
                    active?.turnId === turnId &&
                    feSessionIdRef.current === streamSessionId
                  );
                };

                const patchFromLive = () => {
                  if (!streamStillActive()) {
                    return;
                  }
                  updatePlaceholderRef.current?.({
                    content: live.content,
                    metadata: buildLivePlaceholderMetadata(live),
                  });
                };

                const stopTurnTyping = () => {
                  if (feSessionIdRef.current !== streamSessionId) {
                    return;
                  }
                  setTypingRef.current?.(false);
                };

                const failTurn = (message: string, code?: string | null) => {
                  if (!streamStillActive()) {
                    return;
                  }
                  console.warn("[OsAniSidebar] Turn failed:", message);
                  turnFailedMessage = turnFailureUserMessage(code);
                  live = applyTurnEvent(live, { type: "turn.failed", error: message, code });
                  live = appendClientErrorTimelineText(live, turnFailedMessage);
                  patchFromLive();
                  stopTurnTyping();
                };

                const pollAssistantFromHistory = async () => {
                  const deadline = Date.now() + 120_000;
                  while (Date.now() < deadline && !turnFailedMessage && streamStillActive()) {
                    const messages = await getInAppAgentMessages(streamSessionId);
                    if (!Array.isArray(messages)) {
                      await new Promise((resolve) => setTimeout(resolve, 1500));
                      continue;
                    }
                    const latestAssistant = [...messages]
                      .reverse()
                      .find((message) => {
                        if (message.role !== "assistant" || !message.content?.trim()) {
                          return false;
                        }
                        const createdMs = message.created_at
                          ? new Date(message.created_at).getTime()
                          : 0;
                        return createdMs === 0 || createdMs >= turnStartedAtMs - 5000;
                      });
                    if (!streamStillActive()) {
                      return;
                    }
                    if (latestAssistant?.content) {
                      live = { ...live, content: latestAssistant.content };
                      polledMetadata = mapInAppMessageMetadata(latestAssistant.metadata);
                      updatePlaceholderRef.current?.({
                        content: latestAssistant.content,
                        metadata: polledMetadata,
                      });
                      return;
                    }
                    await new Promise((resolve) => setTimeout(resolve, 1500));
                  }
                };

                setTypingRef.current?.(false);
                updatePlaceholderRef.current?.({
                  content: "",
                  metadata: { timeline: [], thinking: true },
                });

                let turnSubscription: { close: () => void } | null = null;
                activeTurnStreamRef.current = {
                  sessionId: streamSessionId,
                  turnId,
                  close: () => {
                    turnSubscription?.close();
                    // Abandoned mid-turn: stop the worker instead of letting it wait out pending FE tools.
                    void cancelTurn(turnId).catch((cancelError) => {
                      console.warn("[OsAniSidebar] Turn cancel failed:", cancelError);
                    });
                  },
                };

                const subscription = subscribeTurnEvents(turnId, {
                  onEnvelope: (envelope) => {
                    if (!streamStillActive()) {
                      return;
                    }
                    const event = envelope.event;
                    if (event.type === "fe.tools_requested") {
                      return;
                    }
                    if (event.type === "turn.failed") {
                      failTurn(event.error || "Turn failed", event.code);
                      return;
                    }
                    if (event.type === "turn.cancelled") {
                      live = applyTurnEvent(live, event);
                      patchFromLive();
                      stopTurnTyping();
                      return;
                    }
                    const next = applyTurnEvent(live, event);
                    if (next !== live) {
                      live = next;
                      patchFromLive();
                    }
                  },
                  onFeToolsRequested: async (event) => {
                    if (!streamStillActive()) {
                      return;
                    }
                    if (handledFeRequestIds.has(event.request_id)) {
                      return;
                    }
                    handledFeRequestIds.add(event.request_id);
                    live = applyTurnEvent(live, event);
                    patchFromLive();
                    const feToolCalls: FeToolCall[] = event.fe_tool_calls.map((call) => ({
                      tool_id: call.tool_id,
                      params: call.params,
                      instructions: call.instructions,
                    }));
                    try {
                      const toolResults = await executeFeToolCalls(feToolCalls);
                      if (!streamStillActive()) {
                        return;
                      }
                      await submitFeToolResults(turnId, event.request_id, toolResults);
                    } catch (toolError) {
                      console.error("[OsAniSidebar] FE tool batch failed:", toolError);
                      const errorMessage =
                        toolError instanceof Error ? toolError.message : "FE tool execution failed";
                      try {
                        await submitFeToolResults(
                          turnId,
                          event.request_id,
                          feToolCalls.map((call) => ({
                            tool_id: call.tool_id,
                            success: false,
                            error: errorMessage,
                          })),
                        );
                      } catch (submitError) {
                        console.error("[OsAniSidebar] FE tool result submit failed:", submitError);
                        failTurn(errorMessage);
                      }
                    }
                  },
                });
                turnSubscription = subscription;

                try {
                  await subscription.done;
                } catch (sseError) {
                  if (!streamStillActive()) {
                    return {
                      id: `turn-${turnId}-aborted`,
                      content: "",
                      role: "assistant",
                      timestamp: Date.now(),
                      metadata: buildLivePlaceholderMetadata(live),
                      sender: { id: "ani", name: "Ani", type: "assistant" },
                    };
                  }
                  if (!turnFailedMessage) {
                    console.warn("[OsAniSidebar] SSE turn stream failed; polling message history", sseError);
                    await pollAssistantFromHistory();
                  }
                  if (!live.content.trim() && !turnFailedMessage) {
                    throw sseError;
                  }
                } finally {
                  if (
                    activeTurnStreamRef.current?.sessionId === streamSessionId &&
                    activeTurnStreamRef.current?.turnId === turnId
                  ) {
                    activeTurnStreamRef.current = null;
                  }
                  subscription.close();
                  stopTurnTyping();
                }

                if (
                  !live.content.trim() &&
                  !turnFailedMessage &&
                  live.status !== "completed" &&
                  live.status !== "cancelled"
                ) {
                  await pollAssistantFromHistory();
                }

                const finalMetadata = polledMetadata ?? buildLivePlaceholderMetadata(live);

                if (turnFailedMessage) {
                  return {
                    id: `turn-${turnId}-failed`,
                    content: turnFailedMessage,
                    role: "assistant",
                    timestamp: Date.now(),
                    metadata: finalMetadata,
                    sender: { id: "ani", name: "Ani", type: "assistant" },
                  };
                }

                return {
                  id: `turn-${turnId}`,
                  content: live.content,
                  role: "assistant",
                  timestamp: Date.now(),
                  metadata: finalMetadata,
                  sender: { id: "ani", name: "Ani", type: "assistant" },
                };
              } catch (error) {
                console.error("Error sending message:", error);
                throw error;
              }
            }}
            fetchHistory={async (): Promise<ChatComponentMessage[]> => {
              try {
                const messages = await getInAppAgentMessages(feSessionId);
                historyLoadedForRef.current = feSessionId;
                return messages.map((msg) => ({
                  id: msg._id,
                  content: msg.content,
                  role: msg.role,
                  timestamp: msg.created_at ? new Date(msg.created_at).getTime() : Date.now(),
                  actions: msg.actions,
                  attachments: msg.attachments,
                  sources: msg.sources,
                  metadata: mapInAppMessageMetadata(msg.metadata),
                  sender: {
                    id: msg.sender?.id || (msg.role === "assistant" ? "ani" : "user"),
                    name: msg.sender?.name || (msg.role === "assistant" ? "Ani" : "User"),
                    type: msg.sender?.type || (msg.role === "assistant" ? "assistant" : "user"),
                  },
                }));
              } catch (error) {
                console.warn("Failed to load chat history:", error);
                historyLoadedForRef.current = feSessionId;
                return [];
              }
            }}
            fetchSystemTasks={async () => {
              return [];
            }}
            samplePrompts={effectiveSamplePrompts}
            initialAssistantMessage={effectiveInitialMessage}
            inputContainerClassName={OS_ANI_SIDEBAR_INPUT_CLASS}
            onRefreshReady={(refreshFn) => {
              refreshChatRef.current = refreshFn;
            }}
            onSetTypingReady={(setTypingFn) => {
              setTypingRef.current = setTypingFn;
            }}
            onAddMessageReady={(addMessageFn) => {
              addMessageRef.current = addMessageFn;
            }}
            onUpdatePlaceholderReady={(updateFn) => {
              updatePlaceholderRef.current = updateFn;
            }}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Docked Mode - slides in from right, pushes content */}
      {!isFloating && (
        <motion.div
          key="sidebar-wrapper"
          data-testid="os-ani-sidebar-wrapper"
          initial={{ width: 0 }}
          animate={{ width: isOpen ? renderedDockedWidth : 0 }}
          onAnimationComplete={() => {
            // Only show content after width animation completes AND sidebar is opening
            if (isOpen) {
              setShowContent(true);
            }
          }}
          transition={
            isResizing
              ? { duration: 0 }
              : {
                  duration: 0.3,
                  ease: [0.32, 0.72, 0, 1],
                }
          }
          className={`relative z-20 h-full flex-shrink-0 overflow-hidden ${OS_ANI_DOCK_SIDEBAR_SHELL_CLASS}`}
        
        >
          {isOpen && (
            <motion.div
              data-testid="os-ani-sidebar"
              className={`relative flex h-full w-full flex-col ${OS_ANI_DOCK_SIDEBAR_INNER_CLASS} ${OS_ANI_DOCK_CHAT_EDGE_SHADOW_CLASS}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: showContent ? 1 : 0 }}
              transition={{
                duration: 0.2,
                ease: "easeOut"
              }}
              style={{
                width: renderedDockedWidth,
              }}
            >
              <div
                role="separator"
                aria-orientation="vertical"
                aria-valuenow={renderedDockedWidth}
                aria-valuemin={ANI_CHAT_SIDEBAR_MIN_WIDTH_PX}
                aria-valuemax={Math.round(maxDockedWidth)}
                tabIndex={0}
                data-testid="ani-sidebar-resize-handle"
                className="absolute left-0 top-0 bottom-0 z-30 w-1.5 -translate-x-1/2 cursor-col-resize touch-none hover:bg-slate-400/30 dark:hover:bg-slate-500/40"
                onPointerDown={handleResizePointerDown}
                onPointerMove={handleResizePointerMove}
                onPointerUp={handleResizePointerUp}
                onPointerCancel={handleResizePointerUp}
                onDoubleClick={handleResizeDoubleClick}
                onKeyDown={handleResizeKeyDown}
              />
              <AniFileDropZone handler={aniFileDropHandler}>{renderSidebarContent()}</AniFileDropZone>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Floating Mode - draggable window */}
      {isFloating && isOpen && (
        <motion.div
          data-testid="os-ani-sidebar-floating"
          drag
          dragMomentum={false}
          dragElastic={0}
          dragConstraints={{
            top: 0,
            left: 0,
            right: window.innerWidth - ANI_CHAT_SIDEBAR_WIDTH_PX,
            bottom: window.innerHeight - 600
          }}
          onDragStart={() => { isDraggingRef.current = true; }}
          onDragEnd={(_, info) => {
            isDraggingRef.current = false;
            setWidgetViewState({
              floatingPosition: {
                x: info.point.x,
                y: info.point.y
              }
            });
          }}
          initial={{
            opacity: 0,
            scale: 0.96,
            x: widgetViewState.floatingPosition.x || window.innerWidth - ANI_CHAT_SIDEBAR_WIDTH_PX - 24,
            y: widgetViewState.floatingPosition.y || 80
          }}
          animate={{
            opacity: 1,
            scale: 1
          }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{
            duration: 0.15,
            ease: [0.4, 0, 0.2, 1]
          }}
          className={`fixed z-50 flex flex-col overflow-hidden rounded-3xl border pt-14 shadow-lg shadow-black/20 group ${getOsGlassSurfaceClassName(
            OS_ANI_FLOATING_SHELL_CLASS,
          )}`}
          style={{
            width: widgetViewState.floatingSize.width || ANI_CHAT_SIDEBAR_WIDTH_PX,
            height: widgetViewState.floatingSize.height || 600,
            cursor: isDraggingRef.current ? 'grabbing' : 'grab',
            willChange: isDraggingRef.current ? 'transform' : 'auto',
            transformOrigin: 'center center'
          }}
        >
          <AniFileDropZone handler={aniFileDropHandler}>{renderSidebarContent()}</AniFileDropZone>
        </motion.div>
      )}
    </>
  );
}
